import React from 'react';

import { Code } from '@atlaskit/code';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';

import { useExampleFeatureFlags } from '../../example-feature-flag-context';
import type { FeatureFlagSwitcherConfig } from '../../types';

type FeatureFlagSwitcherSectionProps = {
	config: FeatureFlagSwitcherConfig;
	testId?: string;
};

/**
 * __Feature flag switcher section__
 *
 * Renders a single feature-flag switcher in the Styles popover (label + toggle).
 */
const FeatureFlagSwitcherSection = ({
	config,
	testId,
}: FeatureFlagSwitcherSectionProps): React.JSX.Element | null => {
	const context = useExampleFeatureFlags();

	if (!context) {
		return null;
	}

	const { switcherOverrides, setSwitcherFlag } = context;
	const isOn = switcherOverrides[config.flagKey] === true;

	return (
		<Stack space="space.050">
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Stack space="space.025">
					<Text size="small">{config.label}</Text>
					<Text size="small">
						<Code>{config.flagKey}</Code>
					</Text>
				</Stack>
				<Toggle
					id={`${config.flagKey}-toggle`}
					isChecked={isOn}
					onChange={(e) => setSwitcherFlag(config.flagKey, e.target.checked)}
					testId={testId ? `${testId}--${config.flagKey}` : undefined}
				/>
			</Inline>
		</Stack>
	);
};

export default FeatureFlagSwitcherSection;
